import { tooltipFactory, TooltipProvider } from '@milkdown/kit/plugin/tooltip';
import { toggleMark as pmToggleMark } from '@milkdown/kit/prose/commands';
import { setBlockType } from '@milkdown/kit/prose/commands';
import { wrapIn, lift } from '@milkdown/kit/prose/commands';
import type { Ctx } from '@milkdown/kit/ctx';
import {
    usePluginViewContext,
    usePluginViewFactory
} from '@prosemirror-adapter/react';

import { useEffect, useRef } from 'react';

import { ActionIcon, Divider, Group, Tooltip } from '@mantine/core';
import {
    IconBold,
    IconCode,
    IconItalic,
    IconLink,
    IconQuote,
    IconSourceCode,
    IconStrikethrough
} from '@tabler/icons-react';

import './tooltip-plugin.css';

// Create the tooltip plugin pair (spec + prose plugin)
export const formattingTooltip = tooltipFactory('formatting-toolbar');

/**
 * React component rendered inside the floating tooltip.
 * Uses ProseMirror commands directly on the editor view.
 */
function FormattingTooltipView() {
    const { view, prevState } = usePluginViewContext();
    const tooltipRef = useRef<HTMLDivElement>(null);
    const providerRef = useRef<TooltipProvider | null>(null);

    useEffect(() => {
        const el = tooltipRef.current;
        if (!el) return;

        const provider = new TooltipProvider({
            content: el,
            debounce: 50,
            shouldShow: (v) => {
                const { selection } = v.state;
                if (selection.empty) return false;
                const { $from } = selection;
                if ($from.parent.type.name === 'code_block') return false;
                return true;
            }
        });

        providerRef.current = provider;
        return () => {
            provider.destroy();
            providerRef.current = null;
        };
    }, []);

    useEffect(() => {
        providerRef.current?.update(view, prevState);
    });

    const toggleMark = (markName: string) => {
        const markType = view.state.schema.marks[markName];
        if (!markType) return;
        pmToggleMark(markType)(view.state, view.dispatch);
        view.focus();
    };

    const toggleInlineCode = () => {
        const markType = view.state.schema.marks.inlineCode;
        if (!markType) return;
        pmToggleMark(markType)(view.state, view.dispatch);
        view.focus();
    };

    const toggleLink = () => {
        const { state, dispatch } = view;
        const linkType = state.schema.marks.link;
        if (!linkType) return;

        const { from, to } = state.selection;
        let hasLink = false;
        state.doc.nodesBetween(from, to, (node) => {
            if (node.marks.some((m) => m.type === linkType)) hasLink = true;
        });

        if (hasLink) {
            dispatch(state.tr.removeMark(from, to, linkType));
        } else {
            const href = window.prompt('Enter URL:');
            if (!href) return;
            dispatch(state.tr.addMark(from, to, linkType.create({ href })));
        }
        view.focus();
    };

    const convertToCodeBlock = () => {
        const { state, dispatch } = view;
        const codeBlock = state.schema.nodes.code_block;
        if (!codeBlock) return;
        setBlockType(codeBlock)(state, dispatch);
        view.focus();
    };

    const toggleBlockquote = () => {
        const { state, dispatch } = view;
        const blockquote = state.schema.nodes.blockquote;
        if (!blockquote) return;
        // If already in a blockquote, lift out; otherwise wrap
        if (lift(state, dispatch)) {
            view.focus();
            return;
        }
        wrapIn(blockquote)(state, dispatch);
        view.focus();
    };

    // Check which marks are active on the current selection
    const isMarkActive = (markName: string) => {
        const markType = view.state.schema.marks[markName];
        if (!markType) return false;
        const { from, $from, to, empty } = view.state.selection;
        if (empty) return !!markType.isInSet(view.state.storedMarks ?? $from.marks());
        return view.state.doc.rangeHasMark(from, to, markType);
    };

    return (
        <div ref={tooltipRef} className="milkdown-formatting-tooltip" role="toolbar" aria-label="Formatting toolbar" onMouseDown={(e) => e.preventDefault()}>
            <Group gap={2}>
                <Tooltip label="Bold" position="top" withArrow>
                    <ActionIcon
                        variant={isMarkActive('strong') ? 'filled' : 'subtle'}
                        color="gray"
                        size="sm"
                        onClick={() => toggleMark('strong')}
                    >
                        <IconBold size={16} />
                    </ActionIcon>
                </Tooltip>
                <Tooltip label="Italic" position="top" withArrow>
                    <ActionIcon
                        variant={isMarkActive('emphasis') ? 'filled' : 'subtle'}
                        color="gray"
                        size="sm"
                        onClick={() => toggleMark('emphasis')}
                    >
                        <IconItalic size={16} />
                    </ActionIcon>
                </Tooltip>
                <Tooltip label="Strikethrough" position="top" withArrow>
                    <ActionIcon
                        variant={isMarkActive('strike_through') ? 'filled' : 'subtle'}
                        color="gray"
                        size="sm"
                        onClick={() => toggleMark('strike_through')}
                    >
                        <IconStrikethrough size={16} />
                    </ActionIcon>
                </Tooltip>
                <Tooltip label="Inline code" position="top" withArrow>
                    <ActionIcon
                        variant={isMarkActive('inlineCode') ? 'filled' : 'subtle'}
                        color="gray"
                        size="sm"
                        onClick={toggleInlineCode}
                    >
                        <IconCode size={16} />
                    </ActionIcon>
                </Tooltip>
                <Tooltip label="Link" position="top" withArrow>
                    <ActionIcon
                        variant={isMarkActive('link') ? 'filled' : 'subtle'}
                        color="gray"
                        size="sm"
                        onClick={toggleLink}
                    >
                        <IconLink size={16} />
                    </ActionIcon>
                </Tooltip>

                <Divider orientation="vertical" />

                <Tooltip label="Blockquote" position="top" withArrow>
                    <ActionIcon
                        variant="subtle"
                        color="gray"
                        size="sm"
                        onClick={toggleBlockquote}
                    >
                        <IconQuote size={16} />
                    </ActionIcon>
                </Tooltip>
                <Tooltip label="Code block" position="top" withArrow>
                    <ActionIcon
                        variant="subtle"
                        color="gray"
                        size="sm"
                        onClick={convertToCodeBlock}
                    >
                        <IconSourceCode size={16} />
                    </ActionIcon>
                </Tooltip>
            </Group>
        </div>
    );
}

/**
 * Call from MilkdownEditorInner to wire the tooltip plugin.
 * Must be called inside ProsemirrorAdapterProvider.
 */
export function useFormattingTooltip() {
    const pluginViewFactory = usePluginViewFactory();

    return (ctx: Ctx) => {
        ctx.set(formattingTooltip.key, {
            view: pluginViewFactory({ component: FormattingTooltipView })
        });
    };
}
